/* Devocione V7.2 — all help topics stay mounted, preserving native forms and app state. */
(() => {
  'use strict';
  if (customElements.get('dv7-help-center')) return;
  class HelpCenter extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController(); const options = { signal: this.controller.signal };
      this.panels = new Map([...this.querySelectorAll('[data-dv7-help-panel]')].map(panel => [panel.dataset.dv7HelpPanel, panel]));
      this.heading = this.querySelector('[data-dv7-help-title]');
      this.nav = this.querySelector('[data-dv7-help-navigation]'); this.toggle = this.querySelector('[data-dv7-help-toggle]');
      this.mobile = matchMedia('(max-width:749px)'); this.originalTitle = document.title;
      this.classList.add('dv7-help-ready');
      const layoutNav = () => { this.toggle.hidden = !this.mobile.matches; this.nav.hidden = this.mobile.matches; this.toggle.setAttribute('aria-expanded', String(!this.nav.hidden)); };
      this.mobile.addEventListener('change', layoutNav, options); layoutNav();
      this.toggle.addEventListener('click', () => { this.nav.hidden = !this.nav.hidden; this.toggle.setAttribute('aria-expanded', String(!this.nav.hidden)); }, options);
      // Document delegation also handles the same footer links without leaving the center.
      document.addEventListener('click', event => {
        if (!(event.target instanceof Element) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a[href]');
        if (!link || link.hasAttribute('data-dv7-chat') || link.target === '_blank' || link.hasAttribute('download')) return;
        const topic = this.resolveLink(link);
        if (!topic || !this.panels.has(topic)) return;
        event.preventDefault(); this.select(topic, true, true);
      }, options);
      window.addEventListener('popstate', () => this.select(this.hashTopic() || this.dataset.initialTopic, false), options);
      window.addEventListener('hashchange', () => { const topic = this.hashTopic(); if (topic) this.select(topic, false); }, options);
      this.select(this.querySelector('[data-dv7-contact-result]') ? 'contact' : (this.hashTopic() || this.dataset.initialTopic), false);
    }
    hashTopic() {
      let hash = ''; try { hash = decodeURIComponent(location.hash.slice(1)); } catch (_) { return null; }
      if (hash === 'contact_form' || hash === 'Dv7HelpContact') return 'contact';
      return this.panels.has(hash) ? hash : null;
    }
    resolveLink(link) {
      if (link.dataset.dv7HelpLink) return link.dataset.dv7HelpLink;
      let url; try { url = new URL(link.href, location.href); } catch (_) { return null; }
      if (url.origin !== location.origin) return null;
      let hash; try { hash = decodeURIComponent(url.hash.slice(1)); } catch (_) { return null; }
      if (url.pathname === location.pathname && this.panels.has(hash)) return hash;
      if (url.pathname.endsWith('/apps/rastreo') || hash === 'rastreo' && url.pathname.endsWith('/pages/contact')) return 'rastreo';
      for (const [topic, panel] of this.panels) {
        const target = new URL(panel.dataset.topicUrl, location.href);
        if (target.pathname === url.pathname && !url.hash && !url.search) return topic;
      }
      return null;
    }
    select(topic, push = false, focus = false) {
      if (!this.panels.has(topic)) topic = this.panels.keys().next().value;
      if (!topic) return;
      this.activeTopic = topic;
      this.panels.forEach((panel, key) => { panel.hidden = key !== topic; });
      const active = this.panels.get(topic);
      const title = active.dataset.topicTitle || 'Information and help';
      this.heading.textContent = title;
      document.title = `${title} — ${this.dataset.storeName || 'Devocione'}`;
      this.querySelectorAll('[data-dv7-help-link]').forEach(link => {
        if (link.dataset.dv7HelpLink === topic) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
      });
      if (this.mobile.matches) { this.nav.hidden = true; this.toggle.setAttribute('aria-expanded', 'false'); }
      if (push) {
        const url = new URL(location.href); url.hash = topic;
        if (url.href !== location.href) history.pushState({ ...history.state, dv7HelpTopic: topic }, '', url);
      }
      if (topic === 'rastreo') this.loadTracking();
      // The actual app nodes stay mounted. A visibility notification does not re-run third-party scripts.
      active.dispatchEvent(new CustomEvent('dv7:help:visible', { bubbles: true, detail: { topic } }));
      if (focus) {
        this.heading.focus({ preventScroll: true });
        const y = this.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: Math.max(0, y - 16), behavior: matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth' });
      }
    }
    loadTracking() {
      const frame = this.querySelector('[data-dv7-tracking-frame]'); if (!frame || frame.dataset.loaded) return;
      const status = this.querySelector('[data-dv7-tracking-status]');
      const url = new URL(frame.dataset.src, location.href);
      if (url.origin !== location.origin) { status.textContent = 'Open the tracking link to check your order.'; return; }
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      frame.dataset.loaded = 'true'; frame.hidden = false;
      status.textContent = 'Loading the tracking tool…';
      frame.addEventListener('load', () => {
        clearTimeout(this.trackingTimer);
        try {
          const doc = frame.contentDocument;
          if (!doc?.body || doc.URL === 'about:blank') return;
          const main = doc.getElementById('MainContent');
          if (!main) { status.textContent = 'If tracking does not appear, use the link below.'; return; }
          const style = doc.createElement('style');
          style.textContent = '.shopify-section-group-header-group,.shopify-section-group-footer-group,body>footer,body>.announcement-bar,shopify-chat,#ShopifyChat,#preview-bar-iframe,#shopify-preview-bar-iframe{display:none!important}html,body{margin:0!important;padding:0!important;background:#fff!important}body{display:block!important}#MainContent{margin:0!important;padding:0!important;min-height:0!important}';
          doc.head.appendChild(style);
          status.hidden = true;
          const size = () => {
            const height = Math.min(2200, Math.max(420, Math.ceil(main.getBoundingClientRect().height + 32)));
            if (frame.style.height !== `${height}px`) frame.style.height = `${height}px`;
          };
          size(); this.frameObserver?.disconnect();
          if ('ResizeObserver' in frame.contentWindow) { this.frameObserver = new frame.contentWindow.ResizeObserver(size); this.frameObserver.observe(main); }
        } catch (_) { status.textContent = 'If the tool does not load, open the tracking link below.'; }
      }, { signal: this.controller.signal });
      this.trackingTimer = setTimeout(() => { status.textContent = 'The tool is taking longer than expected. You can open the tracking link below.'; }, 15000);
      frame.src = url.href;
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; this.frameObserver?.disconnect(); clearTimeout(this.trackingTimer); }
  }
  customElements.define('dv7-help-center', HelpCenter);
})();
